import { useMemo } from 'react'

import { useAuth } from '@sputnik/auth'

import { useGetApplicationEmails } from './GetApplicationEmails.gql'
import { type NewCollaborator, UseNewApplicationCollaboratorsParams } from './types'

const useNewApplicationCollaborators = ({ salesforceCreditId, skip }: UseNewApplicationCollaboratorsParams) => {
  const { email } = useAuth()

  const { data, loading } = useGetApplicationEmails({
    variables: {
      creditApplicationId: salesforceCreditId,
    },
    skip,
  })

  const collaborators = useMemo((): NewCollaborator[] => {
    if (!email || loading || !data?.creditApplication) {
      return []
    }

    const {
      authorizedSigner,
      beneficialOwners,
      collaborators: rawCollaborators,
      outsourcedAccountant,
    } = data.creditApplication
    const current = new Map<string, NewCollaborator>()

    if (authorizedSigner?.email) {
      current.set(authorizedSigner.email, {
        email: authorizedSigner.email,
        firstName: authorizedSigner.firstName ?? null,
        lastName: authorizedSigner.lastName ?? null,
        role: 'authorizedSigner',
      })
    }

    beneficialOwners?.forEach((owner) => {
      if (owner?.email) {
        current.set(owner.email, {
          email: owner.email,
          firstName: owner.firstName ?? null,
          lastName: owner.lastName ?? null,
          role: 'companyOwner',
        })
      }
    })

    if (outsourcedAccountant?.email) {
      current.set(outsourcedAccountant.email, {
        email: outsourcedAccountant.email,
        firstName: outsourcedAccountant.firstName ?? null,
        lastName: outsourcedAccountant.lastName ?? null,
        role: 'outsourcedAccountant',
      })
    }

    rawCollaborators?.forEach((collaborator) => {
      if (collaborator?.email && !current.has(collaborator.email)) {
        current.set(collaborator.email, {
          email: collaborator.email,
          firstName: collaborator.firstName ?? null,
          lastName: collaborator.lastName ?? null,
          hasLoggedIn: !!collaborator.lastLoginAt,
          id: collaborator.id,
          isInvited: true,
          role: collaborator.createAsAdmin ? 'admin' : 'collaborator',
          createAsAdmin: !!collaborator.createAsAdmin,
        })
      }
    })

    current.delete(email)

    return [...current.values()]
  }, [data, email, loading])

  return { collaborators, isLoadingApplicationCollaborators: loading }
}

export default useNewApplicationCollaborators
